import { inject, injectable } from "tsyringe";
import { TOKEN_IWrapNavigation, type IWrapNavigation } from "./WrapNavigation";
import log from "loglevel";

export const TOKEN_INavigationState = Symbol('INavigationState');

/**
 * Navigation events extracted from URL parameters
 */
export interface State {
    url?: string;
    projectId?: string;
    shareKey?: string;
    symmetricKey?: string;
    allProjects: boolean;
    isRoot: boolean;
    isFork: boolean;
}

/**
 * Service that bridges navigation events to the project bus
 */
export interface INavigationState {
    /**
     * Gets the current navigation state extracted from the URL
     */
    get state(): State;
}

@injectable()
export class NavigationState implements INavigationState {
    private _cache: State = { allProjects: false, isRoot: false, isFork: false };

    constructor(
        @inject(TOKEN_IWrapNavigation) private wrapNavigation: IWrapNavigation
    ) {
    }

    public get state(): State {
        const currentUrl = this.wrapNavigation.url;
        if (this._cache.url === currentUrl) return this._cache;

        log.info("Rebuilding navigation state from current url");
        this._cache.url = currentUrl;

        this._cache.projectId = undefined;
        this._cache.shareKey = undefined;
        this._cache.symmetricKey = undefined;
        this._cache.allProjects = false;
        this._cache.isRoot = false;
        this._cache.isFork = false;

        const pathname: string = this.wrapNavigation.pathName

        if (pathname === "/") {
            this._cache.isRoot = true;
            return this._cache;
        }

        if (pathname === "/projects") {
            this._cache.allProjects = true;
            return this._cache;
        }

        const projectMatch = pathname.match(/\/project\/([^/]+)/);
        if (projectMatch) {
            this._cache.projectId = projectMatch[1];
        }

        const search: URLSearchParams = this.wrapNavigation.urlSearchParams;

        const shareKey = search.get('sharekey');
        if (shareKey) {
            this._cache.shareKey = shareKey;
        }

        const symmetricKey = search.get('sk');
        if (symmetricKey) {
            this._cache.symmetricKey = symmetricKey;
        }

        const fork = search.get('fork');
        if (symmetricKey) {
            this._cache.isFork = fork === "true";
        }

        return this._cache;
    }

}